/**
 * 외부 화재 시 소요 분출용량 산정.
 *
 * 근거: API Standard 521, 2020년 6월판 4.4.13
 *
 *  ── 액체가 있는 용기 (4.4.13.2.4.2) ────────────────────────
 *   식 (7)  적절한 배수·소방설비 O :  Q = C1 × F × Aws^0.82   C1 = 43,200 (SI)
 *   식 (8)  적절한 배수·소방설비 X :  Q = C2 × F × Aws^0.82   C2 = 70,900 (SI)
 *     Q  : 습윤면에 들어가는 총 흡수열량 (W)
 *     F  : 환경계수 (Table 5)
 *     Aws: 총 습윤면적 (m²)
 *   소요 유량은 흡수열량을 배출 조건의 증발잠열로 나눠 구한다.
 *
 *  ── 기체·증기·초임계만 있는 용기 (4.4.13.2.4.3) ────────────
 *   식 (9)   A = F′ × A′ / √P1        ← **유량이 아니라 면적이 바로 나온다**
 *   식 (10)  F′ = C9/(C × Kd) × [ (Tw − T1)^1.25 / T1^0.6506 ]   C9 = 0.2772 (SI)
 *   식 (11)  C  = C10 × √( k × (2/(k+1))^((k+1)/(k−1)) )         C10 = 0.0395 (SI)
 *   식 (12)  T1 = (P1/Pn) × Tn
 *
 * 단위: 면적 m² · 열량 W · 잠열 J/kg · 유량 kg/h · 압력 kPa(절대) · 온도 K
 */

/** 식 (7) 상수 — 적절한 배수·소방설비가 있을 때 (SI, W). */
export const FIRE_C1_SI = 43_200;
/** 식 (8) 상수 — 그렇지 않을 때 (SI, W). */
export const FIRE_C2_SI = 70_900;
/** 식 (10) 상수 (SI). */
export const FIRE_C9_SI = 0.2772;
/** 식 (11) 상수 (SI). */
export const FIRE_C10_SI = 0.0395;
/** 식 (10) 결과가 이보다 작으면 이 값을 쓴다 (SI). */
export const FIRE_F_PRIME_MIN_SI = 182;
/** 식 (10)을 쓸 정보가 부족할 때 쓰는 값 (SI). */
export const FIRE_F_PRIME_DEFAULT_SI = 821;

/**
 * 습윤면적 산정 높이 한계 — 지표(또는 화재가 유지될 수 있는 면)에서 7.6 m.
 * API 521 Table 4. 구형 탱크는 최대 수평 직경과 7.6 m 중 **큰 쪽**을 쓴다.
 */
export const FIRE_HEIGHT_LIMIT_M = 7.6;

/**
 * 환경계수 F — API 521 Table 5.
 *
 * ⚠ 각주 e: **단열되지 않은 용기에는 F 를 적용하지 않는다.**
 *    그런 경우 F = 1.0 으로 둔다. 물 살수설비나 감압·배출설비가 있어도 1.0 이다.
 */
export const ENVIRONMENT_FACTORS = [
  { label: "나관 용기 (단열 없음)", f: 1.0, conductance: null },
  { label: "단열 22.71 W/m²·K (4 Btu/h·ft²·°F)", f: 0.3, conductance: 22.71 },
  { label: "단열 11.36 W/m²·K (2)", f: 0.15, conductance: 11.36 },
  { label: "단열 5.68 W/m²·K (1)", f: 0.075, conductance: 5.68 },
  { label: "단열 3.80 W/m²·K (0.67)", f: 0.05, conductance: 3.8 },
  { label: "단열 2.84 W/m²·K (0.5)", f: 0.0376, conductance: 2.84 },
  { label: "단열 2.27 W/m²·K (0.4)", f: 0.03, conductance: 2.27 },
  { label: "단열 1.87 W/m²·K (0.33)", f: 0.026, conductance: 1.87 },
  { label: "물 살수설비 (나관 용기)", f: 1.0, conductance: null },
  { label: "감압·배출 설비", f: 1.0, conductance: null },
  { label: "복토 저장", f: 0.03, conductance: null },
  { label: "지하 저장", f: 0.0, conductance: null },
] as const;

/* ── 습윤면적 계산 도우미 ─────────────────────────────────
 * 표준은 "어디까지를 습윤면으로 볼 것인가"(Table 4)만 규정하고
 * 면적 자체는 기하학이다. 아래는 기하 계산이며 API 식이 아니다.
 * 지지 스커트로 둘러싸여 환기가 제한된 부분, 배관 표면 포함 여부 등은
 * 설계자가 판단해 별도로 더하거나 빼야 한다(4.4.13.2.2).
 */

/** 습윤 높이에 7.6 m 한계를 적용한다. */
export function cappedWettedHeight(liquidHeightM: number): number {
  return Math.min(liquidHeightM, FIRE_HEIGHT_LIMIT_M);
}

/** 수직 원통 측벽의 습윤면적 (m²). 하부 헤드 면적은 설계자가 따로 더한다. */
export function verticalCylinderWettedArea(
  diameterM: number,
  liquidHeightM: number,
): number {
  return Math.PI * diameterM * cappedWettedHeight(liquidHeightM);
}

/**
 * 수평 원통 동체의 습윤면적 (m²).
 * 액위가 만드는 원호 길이 × 동체 길이. 헤드는 설계자가 따로 더한다.
 */
export function horizontalCylinderWettedArea(
  diameterM: number,
  lengthM: number,
  liquidHeightM: number,
): number {
  const h = Math.max(0, Math.min(cappedWettedHeight(liquidHeightM), diameterM));
  // 액위 h 에 대응하는 중심각 (rad)
  const theta = 2 * Math.acos(1 - (2 * h) / diameterM);
  const arcLength = (diameterM / 2) * theta;
  return arcLength * lengthM;
}

/**
 * 구형 탱크의 습윤면적 (m²).
 *
 * Table 4: **최대 수평 직경까지 또는 7.6 m 중 큰 쪽**까지를 습윤면으로 본다.
 * 화염이 구의 아랫면을 따라 올라가므로 하반구 전체가 노출된다고 본다.
 */
export function sphereWettedArea(
  diameterM: number,
  liquidHeightM: number,
): number {
  const byRule = Math.max(diameterM / 2, FIRE_HEIGHT_LIMIT_M);
  const h = Math.min(Math.max(liquidHeightM, diameterM / 2), byRule, diameterM);
  // 구면 캡의 겉넓이 = π·D·h
  return Math.PI * diameterM * h;
}

/* ── 액체가 있는 용기 (식 7 / 8) ────────────────────────── */

export interface FireWettedInput {
  /** Aws — 총 습윤면적 (m²) */
  readonly wettedAreaM2: number;
  /** F — 환경계수 (Table 5) */
  readonly environmentFactor: number;
  /** 적절한 배수·소방설비가 있는가 → 식 (7), 없으면 식 (8) */
  readonly adequateDrainageAndFirefighting: boolean;
  /** 배출 조건에서의 증발잠열 (J/kg) */
  readonly latentHeatJKg: number;
  /** 용기가 단열되어 있는가. 단열이 없으면 F 는 1.0 이 강제된다(Table 5 각주 e). */
  readonly insulated: boolean;
}

export interface FireWettedSuccess {
  readonly ok: true;
  /** Q — 총 흡수열량 (W) */
  readonly heatAbsorptionW: number;
  /** 소요 분출용량 (kg/h) */
  readonly relievingRateKgH: number;
  /** 실제로 적용된 환경계수 */
  readonly appliedF: number;
  readonly equation: "API 521 (7)" | "API 521 (8)";
  readonly constant: number;
  readonly warnings: readonly string[];
}

export interface FireFailure {
  readonly ok: false;
  readonly reason: "invalid-input";
  readonly message: string;
}

export type FireWettedResult = FireWettedSuccess | FireFailure;

function invalid(message: string): FireFailure {
  return { ok: false, reason: "invalid-input", message };
}

/** 액체가 있는 용기의 화재 소요 분출용량 — API 521 식 (7)/(8). */
export function fireWettedRelievingRate(
  input: FireWettedInput,
): FireWettedResult {
  const {
    wettedAreaM2: Aws,
    environmentFactor: F,
    latentHeatJKg: lambda,
  } = input;

  if (!Number.isFinite(Aws) || Aws <= 0) {
    return invalid("습윤면적은 0보다 커야 합니다.");
  }
  if (!Number.isFinite(F) || F < 0 || F > 1) {
    return invalid("환경계수 F 는 0 이상 1 이하이어야 합니다.");
  }
  if (!Number.isFinite(lambda) || lambda <= 0) {
    return invalid("증발잠열은 0보다 커야 합니다.");
  }

  const warnings: string[] = [];

  // Table 5 각주 e — 단열되지 않은 용기에는 F 를 적용하지 않는다.
  let appliedF = F;
  if (!input.insulated && F !== 1.0) {
    appliedF = 1.0;
    warnings.push(
      "단열되지 않은 용기에는 환경계수를 적용하지 않습니다. F 를 1.0 으로 " +
        "바꿔 계산했습니다(API 521 Table 5 각주 e).",
    );
  }

  const constant = input.adequateDrainageAndFirefighting
    ? FIRE_C1_SI
    : FIRE_C2_SI;
  const heatAbsorptionW = constant * appliedF * Math.pow(Aws, 0.82);
  const relievingRateKgH = (heatAbsorptionW / lambda) * 3600;

  if (input.adequateDrainageAndFirefighting) {
    warnings.push(
      "'적절한 배수'의 판단 기준은 표준이 사용자에게 맡기고 있습니다. " +
        "가연성 액체와 소방수를 용기에서 멀리 흘려보낼 수 있어야 하며, " +
        "근거를 문서로 남겨야 합니다(4.4.13.2.4.2).",
    );
  }
  if (Aws > 0 && input.wettedAreaM2 > 260) {
    warnings.push(
      "습윤면적이 큽니다. 지표에서 7.6 m 를 넘는 부분은 제외했는지, " +
        "지지 스커트로 둘러싸여 환기가 제한된 부분과 연결 배관 표면을 " +
        "포함할지 정했는지 확인하십시오(4.4.13.2.2, Table 4).",
    );
  }

  return {
    ok: true,
    heatAbsorptionW,
    relievingRateKgH,
    appliedF,
    equation: input.adequateDrainageAndFirefighting
      ? "API 521 (7)"
      : "API 521 (8)",
    constant,
    warnings,
  };
}

/* ── 기체·증기·초임계만 있는 용기 (식 9~12) ──────────────── */

export interface FireUnwettedInput {
  /** A′ — 화염에 노출된 용기 표면적 (m²) */
  readonly exposedAreaM2: number;
  /** P1 — 배출 압력 (kPa 절대). 설정압 + 허용 과압 + 대기압. */
  readonly relievingPressureKpaAbs: number;
  /** Pn — 정상 운전 압력 (kPa 절대) */
  readonly normalPressureKpaAbs: number;
  /** Tn — 정상 운전 온도 (K) */
  readonly normalTemperatureK: number;
  /** Tw — 용기 재질의 최대 벽면 온도 (K) */
  readonly maxWallTemperatureK: number;
  /** k — 배출 온도에서의 이상기체 비열비 */
  readonly specificHeatRatio: number;
  /** Kd — 배출계수. 예비 사이징은 0.975. */
  readonly dischargeCoefficient?: number;
  /**
   * 식 (10)을 쓸 정보가 부족한 경우 true.
   * 그러면 F′ = 821 (SI) 을 쓴다.
   */
  readonly useDefaultFPrime?: boolean;
}

export interface FireUnwettedSuccess {
  readonly ok: true;
  /** A — 소요 유효 배출 면적 (mm²). **유량이 아니라 면적이다.** */
  readonly requiredAreaMm2: number;
  /** T1 — 배출 압력에서의 기체 온도 (K), 식 (12) */
  readonly gasTemperatureK: number;
  /** C — 비열비 함수, 식 (11) */
  readonly coefficientC: number;
  /** F′ — 식 (10) 또는 기본값 */
  readonly fPrime: number;
  readonly fPrimeSource: "equation-10" | "minimum-182" | "default-821";
  readonly equation: "API 521 (9)";
  readonly warnings: readonly string[];
}

export type FireUnwettedResult = FireUnwettedSuccess | FireFailure;

/** 식 (11) — 비열비 함수 C (SI). API 520 식 (13)과 같은 형태다. */
export function fireCoefficientC(k: number): number {
  return FIRE_C10_SI * Math.sqrt(k * Math.pow(2 / (k + 1), (k + 1) / (k - 1)));
}

/** 식 (12) — 배출 압력에서의 기체 온도. T1 = (P1/Pn) × Tn */
export function gasTemperatureAtRelieving(
  relievingPressureKpaAbs: number,
  normalPressureKpaAbs: number,
  normalTemperatureK: number,
): number {
  return (relievingPressureKpaAbs / normalPressureKpaAbs) * normalTemperatureK;
}

/**
 * 기체만 있는 용기의 화재 케이스 — API 521 식 (9).
 *
 * ⚠ 이 식은 **소요 면적을 바로 준다.** 액체 케이스처럼 유량을 구해
 * API 520 사이징식에 넣는 방식이 아니다. 단열 효과는 인정하지 않는다.
 */
export function fireUnwettedRequiredArea(
  input: FireUnwettedInput,
): FireUnwettedResult {
  const {
    exposedAreaM2: Aprime,
    relievingPressureKpaAbs: P1,
    normalPressureKpaAbs: Pn,
    normalTemperatureK: Tn,
    maxWallTemperatureK: Tw,
    specificHeatRatio: k,
  } = input;

  const positives: ReadonlyArray<readonly [string, number]> = [
    ["노출 표면적 A′", Aprime],
    ["배출 압력 P1", P1],
    ["정상 운전 압력 Pn", Pn],
    ["정상 운전 온도 Tn", Tn],
    ["최대 벽면 온도 Tw", Tw],
  ];
  for (const [label, v] of positives) {
    if (!Number.isFinite(v) || v <= 0) {
      return invalid(`${label} 은(는) 0보다 큰 유한한 값이어야 합니다.`);
    }
  }
  if (!Number.isFinite(k) || k <= 1) {
    return invalid("비열비 k 는 1보다 커야 합니다.");
  }

  const Kd = input.dischargeCoefficient ?? 0.975;
  if (!Number.isFinite(Kd) || Kd <= 0) return invalid("Kd 가 올바르지 않습니다.");

  const warnings: string[] = [];

  // 식 (12)는 정상 운전 압력에서 배출 압력까지 기체가 가열되며 승압된다는
  // 전제 위에 있다. Pn ≥ P1 이면 그 전제가 깨져 T1 이 Tn 보다 낮게 나오고,
  // 이어지는 F′·A 가 통째로 무의미해진다.
  // 단위를 잘못 넣었을 때(예: kPa 값을 bar 칸에) 여기서 걸린다.
  if (Pn >= P1) {
    return invalid(
      `정상 운전 압력(${Pn.toFixed(1)} kPa abs)이 배출 압력(${P1.toFixed(1)} kPa abs) 이상입니다. ` +
        "배출은 운전 압력보다 높은 압력에서 일어나므로 이 조합은 성립하지 않습니다. " +
        "입력한 압력의 단위를 확인하십시오.",
    );
  }

  const T1 = gasTemperatureAtRelieving(P1, Pn, Tn);

  if (Tw <= T1) {
    return invalid(
      `최대 벽면 온도 Tw(${Tw.toFixed(1)} K)가 배출 온도 T1(${T1.toFixed(1)} K) 이하입니다. ` +
        "화재로 벽면이 기체보다 뜨거워지는 조건이어야 이 식을 쓸 수 있습니다.",
    );
  }

  const C = fireCoefficientC(k);

  let fPrime: number;
  let fPrimeSource: FireUnwettedSuccess["fPrimeSource"];

  if (input.useDefaultFPrime === true) {
    fPrime = FIRE_F_PRIME_DEFAULT_SI;
    fPrimeSource = "default-821";
    warnings.push(
      "식 (10)을 쓸 정보가 부족하다고 하여 F′ = 821 을 적용했습니다. " +
        "벽면 온도와 운전 조건을 확보하면 식 (10)으로 계산하는 편이 정확합니다.",
    );
  } else {
    const computed =
      (FIRE_C9_SI / (C * Kd)) *
      (Math.pow(Tw - T1, 1.25) / Math.pow(T1, 0.6506));
    if (computed < FIRE_F_PRIME_MIN_SI) {
      fPrime = FIRE_F_PRIME_MIN_SI;
      fPrimeSource = "minimum-182";
      warnings.push(
        `식 (10) 결과 ${computed.toFixed(1)} 이 권장 최솟값 182 보다 작아 182 를 적용했습니다(4.4.13.2.4.3).`,
      );
    } else {
      fPrime = computed;
      fPrimeSource = "equation-10";
    }
  }

  warnings.push(
    "이 식은 소요 유량이 아니라 **소요 면적을 직접** 줍니다. 단열 효과는 " +
      "인정하지 않습니다(4.4.13.2.4.3).",
  );
  warnings.push(
    "벽면이 파열 온도에 이르기 전에 배출이 이루어져야 합니다. 감압설비 적용 " +
      "여부도 함께 검토하십시오(4.4.13.2.3, Annex A).",
  );

  return {
    ok: true,
    requiredAreaMm2: (fPrime * Aprime) / Math.sqrt(P1),
    gasTemperatureK: T1,
    coefficientC: C,
    fPrime,
    fPrimeSource,
    equation: "API 521 (9)",
    warnings,
  };
}
